import { localDateOnly } from './localDate';

const dayOf = (report) => String(report.readDate || report.reportedAt || '').slice(0, 10);

const readerKey = (report) => report.readerId ?? report.reader?.id ?? '';

/** "Today", "Yesterday", or a short date ("Sep 28", plus the year if not this year). */
export function dayLabel(day, now = new Date()) {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return 'Earlier';

  const today = localDateOnly(now);
  const yesterday = localDateOnly(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  if (day === today) return 'Today';
  if (day === yesterday) return 'Yesterday';

  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(year === now.getFullYear() ? {} : { year: 'numeric' })
  });
}

/**
 * Collapse one day's reports into rows. Chapters of the same book, read by the
 * same reader, with consecutive numbers become one range; a gap starts a new
 * one. A chapter read twice that day keeps its second read as its own row.
 *
 * Returns entries in the order their first report appeared:
 *   { type: 'single', key, report }
 *   { type: 'range', key, book, from, to, reader, reports } (reports by chapter)
 */
export function collapseChapterRuns(reports) {
  const owner = new Map(); // report -> entry
  const entries = [];
  const layers = new Map(); // readerId|book|layer -> reports

  for (const report of reports) {
    const book = report.chapter?.book;
    const number = report.chapter?.chapter;
    if (!book || typeof number !== 'number') continue;

    // Second and later reads of the same chapter go in a deeper layer so a
    // layer never holds a chapter twice.
    let layer = 0;
    while ((layers.get(`${readerKey(report)}|${book}|${layer}`) || [])
      .some((other) => other.chapter.chapter === number)) {
      layer += 1;
    }
    const key = `${readerKey(report)}|${book}|${layer}`;
    if (!layers.has(key)) layers.set(key, []);
    layers.get(key).push(report);
  }

  for (const group of layers.values()) {
    const sorted = [...group].sort((a, b) => a.chapter.chapter - b.chapter.chapter);
    let run = [];
    const flush = () => {
      if (run.length > 1) {
        const entry = {
          type: 'range',
          key: `range-${run[0].id}`,
          book: run[0].chapter.book,
          from: run[0].chapter.chapter,
          to: run[run.length - 1].chapter.chapter,
          reader: run[0].reader,
          reports: run
        };
        run.forEach((report) => owner.set(report, entry));
      }
      run = [];
    };
    for (const report of sorted) {
      if (run.length && report.chapter.chapter !== run[run.length - 1].chapter.chapter + 1) flush();
      run.push(report);
    }
    flush();
  }

  const seen = new Set();
  for (const report of reports) {
    const entry = owner.get(report);
    if (!entry) {
      entries.push({ type: 'single', key: `single-${report.id}`, report });
    } else if (!seen.has(entry)) {
      seen.add(entry);
      entries.push(entry);
    }
  }
  return entries;
}

/**
 * Newest-first reports grouped by the day they were read, each day collapsed
 * into rows. Pure, so it can be recomputed whenever more reports are loaded.
 */
export function groupReportsByDay(reports, now = new Date()) {
  const days = new Map();
  for (const report of reports) {
    const day = dayOf(report);
    if (!days.has(day)) days.set(day, []);
    days.get(day).push(report);
  }
  return [...days.entries()].map(([day, dayReports]) => ({
    day,
    label: dayLabel(day, now),
    reports: dayReports,
    entries: collapseChapterRuns(dayReports)
  }));
}
